import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';

import { navigateTo } from '../../utils/navigateTo';
import { propTypes } from '../../ThemedPrice';

const inlineStylesHidden = { display: 'none' };
const inlineStylesShown = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  height: '100%',
  width: '100%',
  zIndex: 3
};

const ThemedHomecardWrapper = memo(
  ({
    newTab,
    onClick,
    onMouseEnter,
    onMouseLeave,
    onTouchEnd,
    onTouchStart,
    selected,
    theme,
    link,
    dataTest,
    renderDescription,
    onMoreInfoClick,
    description,
    ...restProps
  }) => {
    const [isDescriptionVisible, setIsDescriptionVisible] = useState(false);
    const homecardTarget = newTab ? '_blank' : '_self';
    const hasDescription = description !== null;

    const handleClick = async e => {
      e && e.preventDefault();
      await onClick();

      navigateTo(link, { newTab });
    };

    const onMoreInfoClickHandler = e => {
      e.stopPropagation();
      e.preventDefault();
      onMoreInfoClick();
      setIsDescriptionVisible(true);
    };

    const onCloseClick = e => {
      e.stopPropagation();
      e.preventDefault();
      setIsDescriptionVisible(false);
    };

    const renderHomecardContent = ({
      renderImage,
      renderContent,
      renderExtraContent
    }) => (
      <>
        {renderImage(restProps)}
        {renderContent({
          ...restProps,
          hasDescription,
          onMoreInfoClick: onMoreInfoClickHandler
        })}
        {renderExtraContent(restProps)}
      </>
    );

    if (link) {
      return (
        <>
          <a
            data-test={dataTest}
            href={link}
            className={theme.wrapper}
            onClick={handleClick}
            target={homecardTarget}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onTouchEnd={onTouchEnd}
            onTouchStart={onTouchStart}
          >
            {renderHomecardContent(restProps)}
          </a>
          {hasDescription && (
            <div
              style={
                !isDescriptionVisible ? inlineStylesHidden : inlineStylesShown
              }
            >
              {renderDescription({
                ...restProps,
                description,
                link,
                linkTarget: homecardTarget,
                onCloseClick
              })}
            </div>
          )}
        </>
      );
    }

    return (
      <div className={theme.wrapper}>{renderHomecardContent(restProps)}</div>
    );
  }
);

ThemedHomecardWrapper.propTypes = {
  className: PropTypes.string,
  link: PropTypes.string,
  newTab: PropTypes.bool,
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onTouchEnd: PropTypes.func,
  onTouchStart: PropTypes.func,
  renderContent: PropTypes.func,
  renderImage: PropTypes.func,
  selected: PropTypes.bool,
  theme: PropTypes.shape({
    wrapper: PropTypes.string
  }),
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string
    })
  ),
  renderExtraContent: PropTypes.func,
  renderDescription: PropTypes.func,
  dataTest: propTypes.string,
  onMoreInfoClick: PropTypes.func,
  description: PropTypes.shape({})
};

ThemedHomecardWrapper.defaultProps = {
  className: '',
  link: '',
  newTab: true,
  onClick: () => {},
  onMouseEnter: () => {},
  onMouseLeave: () => {},
  onTouchEnd: () => {},
  onTouchStart: () => {},
  renderContent: () => {},
  renderImage: () => {},
  selected: false,
  theme: {},
  photos: [],
  renderExtraContent: () => {},
  renderDescription: () => {},
  dataTest: '',
  onMoreInfoClick: () => {},
  description: null
};

export default ThemedHomecardWrapper;
